import { z } from 'zod';
import { Timestamp, Uuid } from './common.js';
import { ExtractorEffort, ExtractorTier, OrgResearchDepth } from './org-settings.js';
import {
  AdjudicationSensitivity,
  AnalysisKind,
  AutoContinue,
  FacetKeyLoose,
  FacetMode,
  ResearchPass,
  RunKind,
  RunState,
} from './vocab.js';

/* ————————————————————————— config ————————————————————————— */

/**
 * One leg of `config.initial[]` (server-design.md §5.2). `facet` is a registry
 * key validated strictly server-side.
 *
 * `targets[]` is typed as strings pending a decision on what a targeted-mode
 * target actually is — the dashboard has no source for them, which is why the
 * refresh picker uses `mode: 'update'` + `since` (ui-design.md §8 item 20).
 */
export const FacetLeg = z
  .object({
    facet: FacetKeyLoose,
    mode: FacetMode,
    targets: z.array(z.string()).optional(),
    since: Timestamp.optional(),
  })
  .strict();
export type FacetLeg = z.infer<typeof FacetLeg>;

/**
 * `auto_bind` is written `on|off` in server-design §5.2; on the wire it is a
 * boolean (JSON-native, and the run row materializes whatever the server
 * defaults it to).
 */
export const QuickSearchRunConfigInput = z
  .object({
    auto_continue: AutoContinue.optional(),
    auto_bind: z.boolean().optional(),
  })
  .strict();
export type QuickSearchRunConfigInput = z.infer<typeof QuickSearchRunConfigInput>;

/**
 * `analysis_kinds[]` is deliberately absent from the input: the org's vertical
 * module derives it from subject status (server-design.md §7.9a). A client never
 * chooses analysis kinds.
 *
 * Nor the facet plan or the rounds (run defaults sweep, 2026-09-26): the
 * subject's depth decides both. A client that could name `update` legs or four
 * rounds could buy a dossier at the re-check's price.
 */
export const ResearchRunConfigInput = z
  .object({
    /**
     * **Re-check everything since this instant** (the Refresh modal, credits
     * launch spec step 2): every facet this person's depth plans, each as an
     * `update` leg with this `since`. The server resolves the facets, so a
     * member's light pass is re-checked over its own three rather than all six.
     * A re-check is a single pass: a dossier's follow-up rounds are not a
     * re-check.
     */
    recheck_since: Timestamp.optional(),
    adjudication_sensitivity: AdjudicationSensitivity.optional(),
    auto_bind: z.boolean().optional(),
  })
  .strict();
export type ResearchRunConfigInput = z.infer<typeof ResearchRunConfigInput>;

/**
 * A prose rerun chooses **nothing** (prose-rerun-design.md §4.3). Its whole
 * input is the record the subject already holds: the facet summaries, the facts
 * as the office has left them, and the knobs the server re-resolves at creation
 * (`analysis_kinds` from the subject's status, and the org's own prose —
 * `club_values`, `org_profile` — from settings). There is no facet plan to pick, no round clamp to set, no
 * sensitivity to choose — so the input object is empty rather than absent, and
 * `.strict()` turns a client that sends one of the research knobs into a 400
 * instead of a silently ignored field.
 */
export const ProseRefreshRunConfigInput = z.object({}).strict();
export type ProseRefreshRunConfigInput = z.infer<typeof ProseRefreshRunConfigInput>;

/**
 * **An organisation lookup chooses one thing: how deep** (organisations-as-
 * subjects note §1 R3a, §2 chunk 3 "As grounded" 2).
 *
 * The one field is the ruling. For a person every knob on `ResearchRunConfigInput`
 * exists because something upstream — a status, a settings row, a facet refresh
 * — has an opinion the client sometimes has to override. An organisation has no
 * status, so nothing upstream has an opinion about depth and the office states
 * it directly; and everything *else* about the run (which facets, how many
 * searches and fetches, how many people, which analysis kinds, the tier, the
 * effort, the cap) is derived from that word and from the org's settings, which
 * is why none of them is a body field.
 *
 * Absent means {@link DEFAULT_ORG_RESEARCH_DEPTH} — `lookup`, the cheaper of the
 * two. `.strict()` so a client reaching for a person's knobs earns a 400 rather
 * than a silently dropped field.
 */
export const OrgLookupRunConfigInput = z
  .object({
    depth: OrgResearchDepth,
  })
  .strict();
export type OrgLookupRunConfigInput = z.infer<typeof OrgLookupRunConfigInput>;

/**
 * The materialized config on the run row (server-side shape, kept here so the
 * server has one definition). It is not part of any dashboard response.
 */
export const ResearchRunConfig = z.object({
  initial: z.array(FacetLeg),
  max_rounds: z.number().int().min(0),
  /**
   * **The extractor's model tier and effort** (§5.2, 2026-09-03), resolved from
   * `org.settings.research` at creation with the constants in
   * `@ni/shared/org-settings` as the floor.
   *
   * **Required, always a string**, unlike the org's prose above: an absent key
   * would leave every reader to apply a default of its own, and two defaults is
   * how a wire comes to disagree with itself. Unset is expressed by the org not
   * writing one, which is resolved here and never travels.
   *
   * **The extractor only.** The summary legs stay Opus by design — they write the
   * prose the office reads — and nothing on this object moves them.
   */
  extractor_tier: ExtractorTier,
  extractor_effort: ExtractorEffort,
  /**
   * Pages one facet leg may fetch, in the initial pass and in a follow-up round.
   * Derived from the subject's depth (`runs/research-depth.ts`), never chosen per
   * run. `page_budget_followup` is `0` at a depth that takes no rounds, except on
   * a Refresh, whose update legs read it (`REFRESH_PAGE_BUDGET`).
   */
  page_budget_initial: z.number().int().nonnegative(),
  page_budget_followup: z.number().int().nonnegative(),
  /**
   * The run's spend cap in micro-USD — the unit `ai_usage_event.cost_micro_usd`
   * meters in, so the pipeline compares two integers. **`null` is no cap**, and
   * it is `null` rather than absent for the reason the tier is a string rather
   * than sometimes missing.
   */
  budget_micro_usd: z.number().int().positive().nullable(),
  adjudication_sensitivity: AdjudicationSensitivity,
  analysis_kinds: z.array(AnalysisKind),
  auto_bind: z.boolean(),
  /**
   * The club's own values, snapshotted from `org.settings.club_values` at
   * creation (2026-08-30, Soroush). **Absent when the org has written none** —
   * the pipeline's `club_fit` prompt handles both cases, and absent is the one it
   * was already written for. Not in `ResearchRunConfigInput`: it lands verbatim in
   * a model's user turn, so it is an org setting and never a per-run body field.
   */
  club_values: z.string().optional(),
  /**
   * The organisation as it describes itself, snapshotted from
   * `org.settings.org_profile` at creation (2026-09-02, verticals note §0.8).
   * Same §5.2 mechanics as the key above and the same omission rule — **absent
   * when the org has written none** — and the same reason for not being an
   * input: it lands verbatim in a model's user turn.
   *
   * What it is *not* is the key above under another name. Values are a lens one
   * vertical holds a person against; a profile is who the organisation is, which
   * every vertical has, so this one is core and rides on every org's run.
   */
  org_profile: z.string().optional(),
  /**
   * **The protection account this subject belongs to**, snapshotted at creation
   * (security fold, 2026-09-22) — the security vertical's own `runConfigExtras`,
   * beside the club's `club_values` and on the same §5.2 terms.
   *
   * It is the account's `label` and `engagement_context`: who the firm is
   * protecting and what the engagement is about, which is the framing the two
   * security analysis kinds are written against. It is **absent in every other
   * vertical**, and absent in a security org is impossible — every record in one
   * belongs to an account.
   *
   * An object rather than a bare paragraph because the label is part of the
   * framing and not decoration: "the Halloran account" is what the firm calls
   * this engagement, and the prose the model writes should be able to use its
   * own customer's word for it.
   *
   * Deliberately *not* the org's `org_profile` under another name. A firm's
   * statement about itself would be one paragraph for eight engagements; this is
   * one per account, which is the only level at which a protective read has a
   * subject it can be held against.
   */
  security_account: z.object({ label: z.string(), engagement_context: z.string() }).optional(),
  /**
   * **Ask the data broker what it is selling about this person** (security
   * audit §6, 2026-09-22) — the security vertical's second `runConfigExtras`
   * key, written for a `principal` and for nobody else.
   *
   * `z.literal(true).optional()` and never a boolean, because there are two
   * answers here and not three: the check is owed, or the question does not
   * arise. An associate is on the account because they are around the
   * principal and a subject of interest is being looked at rather than
   * protected, so a `false` on either of them would be the vertical asserting
   * something it has no opinion about. The pipeline reads the key as "is it
   * exactly true", which makes absent and off one answer for every reader.
   *
   * **On a research run and on no other kind.** The pipeline's exposure leg is
   * the only thing that reads it and it runs during collection, so a prose
   * rewrite — which enters at the summary stage and collects nothing — carries no
   * such key: an instruction nothing on that path can carry out is worse than an
   * absent one. The vertical is told which kind of run it is materializing
   * (`RunConfigContext.kind`) precisely so it can say so.
   */
  exposure_check: z.literal(true).optional(),
});
export type ResearchRunConfig = z.infer<typeof ResearchRunConfig>;

export const QuickSearchRunConfig = z.object({
  auto_continue: AutoContinue,
  auto_bind: z.boolean(),
});
export type QuickSearchRunConfig = z.infer<typeof QuickSearchRunConfig>;

/**
 * What a prose rerun's row actually carries (prose-rerun-design.md §4.3). The
 * narrative kinds and the prose the summary stage reads them against, and no
 * more: the run enters at the summary stage, so there is nothing else for it to
 * obey. All of it is **re-resolved at creation** rather than copied from the run
 * being rewritten — that re-resolution is what makes an office that edited its
 * values, or its own description, since the dossier completed get a reading
 * against what it holds today.
 *
 * `initial`, `max_rounds`, `adjudication_sensitivity` and `auto_bind` are all
 * absent on purpose: there is no facet pass to plan, no orchestrator loop to
 * clamp, no claim to adjudicate and no identity to bind.
 */
export const ProseRefreshRunConfig = z.object({
  analysis_kinds: z.array(AnalysisKind),
  /**
   * **What a rewrite spends, and nothing about what it fetches** (2026-09-03).
   *
   * The depth-derived keys — `initial`, `max_rounds`, the page budgets — stay
   * absent, because a run that enters at the summary stage must not fetch and a
   * budget for pages it will never read is an invitation to read them.
   *
   * These three are present so **both arms put one shape on the wire**: a key
   * that exists on one kind of run and not another is a key every reader has to
   * branch on. Only `budget_micro_usd` has a reader on this path — a rewrite runs
   * the summary stage and no extraction at all, so nothing here consults the tier
   * or the effort. They are carried for the shape, and are already correct the
   * day a rewrite grows a reader for them.
   */
  extractor_tier: ExtractorTier,
  extractor_effort: ExtractorEffort,
  budget_micro_usd: z.number().int().positive().nullable(),
  club_values: z.string().optional(),
  org_profile: z.string().optional(),
});
export type ProseRefreshRunConfig = z.infer<typeof ProseRefreshRunConfig>;

/* ————————————————————————— requests ————————————————————————— */

/**
 * Live charging only (credits launch spec, steps 1–2), on every priced kind.
 * `admission_key` makes a retried press return the run the first press admitted
 * rather than a second one. `expected_credit_units` is the price the button
 * showed, in ledger units; an account enrolled in charging refuses a press whose
 * price is now higher (`CREDIT_PRICE_CHANGED`), and an absent value accepts only
 * a free admission (a quick look within the monthly allowance). Accounts not
 * enrolled ignore both.
 */
const Charging = {
  admission_key: z.uuid().optional(),
  expected_credit_units: z.number().int().min(0).optional(),
};

/** `POST /subjects/:id/runs`. */
export const CreateRunBody = z.discriminatedUnion('kind', [
  z
    .object({
      kind: z.literal('quick_search'),
      config: QuickSearchRunConfigInput.optional(),
      ...Charging,
    })
    .strict(),
  z
    .object({
      kind: z.literal('research'),
      config: ResearchRunConfigInput.optional(),
      ...Charging,
    })
    .strict(),
  z
    .object({
      kind: z.literal('prose_refresh'),
      config: ProseRefreshRunConfigInput.optional(),
    })
    .strict(),
  /**
   * The organisation's own arm. `config` is optional and its `depth` is not:
   * a body that names no config takes `lookup`, and one that names a config
   * has come to say which of the two depths it wants.
   */
  z
    .object({
      kind: z.literal('org_lookup'),
      config: OrgLookupRunConfigInput.optional(),
      ...Charging,
    })
    .strict(),
  /**
   * **An event's research** (event graph design §1 ruling 2). No config: one
   * fixed plan, resolved by the server, so a run costs the same whoever presses.
   */
  z
    .object({
      kind: z.literal('event_research'),
      ...Charging,
    })
    .strict(),
]);
export type CreateRunBody = z.infer<typeof CreateRunBody>;

/* ————————————————————————— responses ————————————————————————— */

/** The dashboard's live-progress channel: a line of text, never a progress bar. */
export const RunProgress = z.object({
  stage: z.string(),
  note: z.string().nullable(),
});
export type RunProgress = z.infer<typeof RunProgress>;

/**
 * `{planned[], completions: [{facet, round}]}` — completions accumulate across
 * rounds, so the strip filters them to `current_round` before counting.
 */
export const ResearchRoundPlan = z
  .object({
    round: z.number().int().nonnegative(),
    jobs: z
      .array(
        z
          .object({
            facet: z.string().min(1),
            mode: z.enum(['full', 'targeted', 'update']),
            targets: z.array(z.string()),
            brief: z.string().nullable().default(null),
          })
          .strict(),
      )
      .max(32),
  })
  .strict();
export type ResearchRoundPlan = z.infer<typeof ResearchRoundPlan>;

export const FacetCoverage = z
  .object({
    source_checks: z
      .array(
        z
          .object({
            tool: z.string(),
            complete: z.boolean(),
            details: z.array(z.record(z.string(), z.unknown())),
          })
          .strict(),
      )
      .max(100)
      .optional(),
    records_checks: z.number().int().nonnegative().optional(),
    facet: z.string().min(1),
    round: z.number().int().nonnegative(),
    status: z.enum(['complete', 'empty', 'partial', 'unavailable', 'skipped', 'failed', 'unknown']),
    documents: z.number().int().nonnegative(),
    claims: z.number().int().nonnegative(),
    caveats: z.array(z.string()).max(100),
  })
  .strict();
export type FacetCoverage = z.infer<typeof FacetCoverage>;

/** Extractive organization analysis input, banked with a completed research leg. */
export const OrgAnalysisDigest = z
  .object({
    passages: z
      .array(
        z
          .object({
            evidence_id: z.guid(),
            source_url: z.string().nullable().optional(),
            start: z.number().int().nonnegative(),
            end: z.number().int().positive(),
            excerpt: z.string().min(1).max(750),
          })
          .strict(),
      )
      .max(8),
    excluded_evidence_ids: z.array(z.guid()).max(100),
  })
  .strict();
export type OrgAnalysisDigest = z.infer<typeof OrgAnalysisDigest>;

export const FacetMap = z.object({
  rounds: z.array(ResearchRoundPlan).optional(),
  coverage: z.array(FacetCoverage).optional(),
  planned: z.array(FacetKeyLoose),
  completions: z.array(
    z.object({
      facet: FacetKeyLoose,
      round: z.number().int().nonnegative(),
      digest: OrgAnalysisDigest.optional(),
    }),
  ),
});
export type FacetMap = z.infer<typeof FacetMap>;

/**
 * **Where a failed run's Resume stands**, derived by the server at read
 * (`server/src/modules/runs/run-resume.ts`). It is the one signal every surface
 * that offers Resume, shows a resume waiting, or decides whether to offer a new
 * run instead reads, so no page holds an opinion of its own.
 *
 * Null when the run is not one the server would resume: it is not `failed`, or
 * it failed in a way Resume refuses (identity withdrawn, cancelled by the office,
 * unreadable identity match, a report run).
 *
 *  - `available` — failed and resumable, with no resume outstanding.
 *    `requested_at` is null.
 *  - `queued` — a Resume was accepted at `requested_at` and the agent has not
 *    claimed the run yet. Starting an agent container takes from about a minute
 *    to over twelve, and the row stays `failed` (with the earlier error) until
 *    the claim takes it to `running`, so this is what says "waiting for the
 *    agent" in that gap.
 *  - `not_picked_up` — a Resume was accepted at `requested_at` and nothing
 *    claimed the run within the window the reaper gives a new run to be picked
 *    up (`REAPER_CREATED_OVERDUE_MINUTES`). The run is still failed and
 *    resumable; the reaper never sweeps a failed run, so nothing else will move
 *    it.
 */
export const RunResume = z
  .object({
    status: z.enum(['available', 'queued', 'not_picked_up']),
    requested_at: Timestamp.nullable(),
  })
  .strict();
export type RunResume = z.infer<typeof RunResume>;

/** `GET /runs/:id` — the polled read (server-design.md §3.2). */
export const RunDetail = z.object({
  id: Uuid,
  kind: RunKind,
  state: RunState,
  progress: RunProgress.nullable(),
  facet_map: FacetMap,
  current_round: z.number().int().nonnegative(),

  /**
   * The adjudication counter behind "2 of 3 reviewed" — **every**
   * `claim_adjudication` row on the run, blocking and queued alike. Populated
   * for a research run in or after `awaiting_adjudication`, `null` otherwise —
   * a run that asked nothing has nothing to count. It lives on the polled read
   * so the pause's only honest feedback comes from one place: partially
   * answered means the server does not invoke, and nothing else visibly moves.
   *
   * Queued rows joined the count on 2026-08-31 (§3f): a run now parks before its
   * summary while **any** adjudication is open, so "N findings need your review"
   * has to mean all of them or the strip lies about what the run is waiting on.
   */
  adjudications: z
    .object({
      answered: z.number().int().nonnegative(),
      total: z.number().int().nonnegative(),
    })
    .nullable(),

  /**
   * Whether a **pending gating decision** stands on this run — the same set
   * `server/src/modules/decisions/gating.ts` computes for the continuation's leg
   * name, so the client cannot hold a second opinion about what a run is waiting
   * on.
   *
   * It exists because an `awaiting_*` state on its own does not say whether the
   * office still owes an answer. Answering the last gate latches the decision and
   * enqueues the continuation but writes **no** run state — the flip to `running`
   * only happens when the leg's invocation claims the run, seconds to tens of
   * seconds later (dispatch + cold start). So `awaiting_* && !gated` is exactly
   * the "Resuming…" window (ui-design §4.3), and reading it off the polled run
   * makes that window survive a reload and hold in a second tab, which the
   * in-memory mark it replaces could not.
   *
   * Factual rather than state-scoped: it answers "are there pending gating rows
   * on this run", full stop. A terminal run can carry `true` — a queued question
   * can outlive the run that raised it — and the surfaces that care read it only
   * in the awaiting states.
   */
  gated: z.boolean(),

  /**
   * A pipeline-reported failure, the reaper's verdict, or the withdrawal a
   * wrong-person answer wrote. `message` is what the office reads; `code` is
   * what the surface branches on, and is null for a failure that carried none.
   * `IDENTITY_WITHDRAWN` is the code that makes a failed run inert history —
   * no strip, no Resume — rather than work to pick back up, and
   * `CANCELLED_BY_OFFICE` (2026-09-10) is the same reading one door over: the
   * office pressed Cancel, so the run is history and the page goes back to
   * offering the thing that starts one.
   */
  error: z.object({ code: z.string().nullable(), message: z.string() }).nullable(),

  /** Where this run's Resume stands; see {@link RunResume}. */
  resume: RunResume.nullable(),

  /**
   * The last time anything on this run reported in — seeded at row creation, so
   * it is always present. It rides the polled read because the strip owes the
   * office **staleness honesty**: a run that has genuinely gone quiet says so,
   * and a run that stopped between legs says `checkpointed` instead of
   * masquerading as silence.
   */
  heartbeat_at: Timestamp,

  /**
   * The run's own timeline, appended server-side and never trimmed: `t:'stage'`
   * on a progress-**stage change**, `t:'state'` on every accepted transition.
   *
   * Per-facet stages (`facet:*`) are deliberately left out — a six-facet pass
   * would otherwise bury the four lines that describe the run's shape under
   * dozens that describe one facet's, and that detail already has a better home
   * in `facet_progress`. What is left is naturally bounded (`max_rounds` clamps
   * the rounds), which is why there is no cap.
   */
  stage_history: z.array(
    z
      .object({
        t: z.enum(['stage', 'state']),
        v: z.string(),
        at: Timestamp,
      })
      .strict(),
  ),

  /**
   * Per-facet narration, keyed by facet key. A planned facet **absent from the
   * map is pending** — the client derives that rather than the server posting
   * it, which is the same reasoning that keeps queued facets out of the
   * "running" count: the pipeline says nothing at fan-out because nothing is
   * happening yet.
   *
   * `phase` walks `retrieving → extracting → posting → complete`. The pipeline
   * posts the first three; **`complete` is the server's**, stamped when it
   * processes the matching `facet_complete` (with the last reported `docs`
   * preserved), so the ledger and the facet map can never disagree about which
   * facets are done. `extracting` may be skipped where the boundary has no clean
   * hook. `docs` is the working-set document count where one is known.
   */
  facet_progress: z
    .record(
      z.string(),
      z
        .object({
          phase: z.enum(['retrieving', 'extracting', 'posting', 'complete']),
          docs: z.number().int().nonnegative().nullable(),
          round: z.number().int().nonnegative(),
          at: Timestamp,
        })
        .strict(),
    )
    .nullable(),

  /**
   * What the run's deduplication actually did: `resolutions` is every
   * `dedup_resolution` recorded for this run, `llm_adjudicated` the subset an
   * adjudicator model decided rather than the deterministic path. Null for a
   * quick_search and for a research run that resolved nothing — a run with
   * nothing to say about dedup says nothing, rather than showing a row of zeroes.
   */
  dedup: z
    .object({
      resolutions: z.number().int().nonnegative(),
      llm_adjudicated: z.number().int().nonnegative(),
    })
    .nullable(),

  /**
   * Which pass a `research` run was (`ResearchPass`), null on every other kind.
   * Derived at read: the config's legs and whether an earlier research run of
   * this subject had completed when this one was created.
   */
  research_pass: ResearchPass.nullable(),
  /**
   * **Whether a completed run delivered**, off its delivery receipt: the same
   * answer charging settles from, so a run's page and the credits page agree.
   * False is a run that finished with nothing to show (no organization or event
   * found, nobody screened). Null while it is not complete, and on a run whose
   * receipt predates this record.
   */
  delivered: z.boolean().nullable(),
  /** Null for server-created runs (auto-continue) — "continued automatically". */
  created_by_user_id: Uuid.nullable(),
  created_at: Timestamp,
  completed_at: Timestamp.nullable(),
});
export type RunDetail = z.infer<typeof RunDetail>;

/** The compact form carried on the record composition and in run history. */
export const RunSummary = z.object({
  id: Uuid,
  kind: RunKind,
  state: RunState,
  /**
   * Same shape as `RunDetail.error`. It rides the summary because the record
   * composition and run history both have to tell a withdrawn run from a
   * resumable one without a second fetch per row.
   */
  error: z.object({ code: z.string().nullable(), message: z.string() }).nullable(),
  /** As {@link RunDetail}'s: the record page offers Resume or a new run off it. */
  resume: RunResume.nullable(),
  /**
   * **Which of an organisation's two depths this run was** — off the run's own
   * frozen config (`OrgLookupRunConfigInput.depth`), and null on every other
   * kind. It is named for the kind it belongs to rather than `depth`, because a
   * person's research run has a depth as well and it is a different vocabulary:
   * a field called `depth` answering null on a `research` row would be a lie
   * about a run that has one.
   *
   * It rides the summary because the organisation's record page decides what to
   * offer next out of the run *list* and nothing else: an investigation that
   * finished three runs ago is what puts Refresh in the foot, and a failed run
   * on top of it must still leave the record where the last completed run left
   * it. {@link RunDetail} lets the strip read the same thing off `facet_map`
   * (`orgLookupDepth`), which is all the strip has — but a list of summaries has
   * no facet map in it, and re-deriving one per row would be a second opinion
   * about a value the run already froze.
   */
  lookup_depth: OrgResearchDepth.nullable(),
  /**
   * Which pass a `research` run was (`ResearchPass`), null on every other kind.
   * Derived at read: the config's legs and whether an earlier research run of
   * this subject had completed when this one was created.
   */
  research_pass: ResearchPass.nullable(),
  /** As {@link RunDetail}'s. */
  delivered: z.boolean().nullable(),
  created_by_user_id: Uuid.nullable(),
  created_at: Timestamp,
  completed_at: Timestamp.nullable(),
});
export type RunSummary = z.infer<typeof RunSummary>;

/** `GET /subjects/:id/runs`. */
export const RunListResponse = z.object({
  entries: z.array(RunSummary),
});
export type RunListResponse = z.infer<typeof RunListResponse>;

/**
 * **What one later run found** (dossier Re-run and Refresh spec §3.5): a completed
 * Re-run or Refresh, and the facts its claims resolved to, grouped by what each
 * was against the record. Derived at read from the run's claims; the record and
 * the brief stay one current document. The first dossier is never an entry.
 *
 * A lineage is listed once: `new` if the run minted it, else `changed` if a claim
 * moved its severity, else `confirmed`. `coverage` is the run's first pass, one
 * row per facet.
 */
export const RunFindings = z.object({
  run_id: Uuid,
  research_pass: z.enum(['rerun', 'refresh']),
  /** A Refresh's `since`; null on a Re-run. */
  since: Timestamp.nullable(),
  completed_at: Timestamp,
  new: z.array(Uuid),
  confirmed: z.array(Uuid),
  changed: z.array(Uuid),
  coverage: z.array(z.object({ facet: z.string(), status: FacetCoverage.shape.status }).strict()),
});
export type RunFindings = z.infer<typeof RunFindings>;

/** `GET /subjects/:id/runs/findings`, newest first. */
export const RunFindingsResponse = z.object({
  entries: z.array(RunFindings),
});
export type RunFindingsResponse = z.infer<typeof RunFindingsResponse>;

/** `POST /subjects/:id/runs` and `POST /runs/:id/resume` both return the run row. */
export const CreateRunResponse = RunDetail;
export type CreateRunResponse = z.infer<typeof CreateRunResponse>;

/** Resume's optional body: the price the button showed, as on a quick-search press. */
export const ResumeRunBody = z
  .object({ expected_credit_units: z.number().int().min(0).optional() })
  .strict();
export type ResumeRunBody = z.infer<typeof ResumeRunBody>;
export const ResumeRunResponse = RunDetail;
export type ResumeRunResponse = z.infer<typeof ResumeRunResponse>;

/**
 * `POST /runs/:id/cancel` — the office stopping a run it started (owner ruling,
 * 2026-09-10). The same row every other run route answers with, read back after
 * the stop, so the strip that made the call renders the run's own ending rather
 * than a receipt: `state` is `failed` and `error.code` is `CANCELLED_BY_OFFICE`.
 */
export const CancelRunResponse = RunDetail;
export type CancelRunResponse = z.infer<typeof CancelRunResponse>;

/** `STATE_CAS_FAILED` carries the state the caller lost the race to. */
export const StateCasFailedDetails = z.object({
  currentState: RunState,
});
export type StateCasFailedDetails = z.infer<typeof StateCasFailedDetails>;
